import { useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { TabView, TabPanel } from 'primereact/tabview';
import { Tooltip } from 'primereact/tooltip';
import { useScenarioContext, scenarioActions } from '../../context/ScenarioContext';
import ApiService, { handleApiError } from '../../services/api';
import { usePracticeData } from '../../hooks/usePracticeData';
import FeeScheduleModal from './FeeScheduleModal';
import PayerGroupingUpload from './PayerGroupingUpload';
import { useToast } from '../../context/ToastContext';
import { PayerGroupingReconciliation } from '../../types';


// Styles
import './AccountSettings.css';

// Normalize a group name for name-based matching — must mirror the backend
// _norm_group_key so rename/removal reconciliation lines up with fee-schedule keys.
const normGroup = (s: string): string =>
  String(s || '')
    .trim()
    .toLowerCase()
    .replace(/[\s\-.,;&()/_]/g, '');

// A group that was removed in this edit session and still has fee-schedule rates.
// A removed group's rates must be reassigned or deleted — never left orphaned.
type RemovedGroupDecision = {
  group: string;
  familyCount: number;
  action: 'reassign' | 'drop';
  target?: string;
};

type ReconcilePlan = {
  // Renamed groups whose rates carry over automatically (shown for transparency).
  renames: { from: string; to: string; familyCount: number }[];
  // Removed groups that still have rates — the user must decide what happens.
  removed: RemovedGroupDecision[];
};

type PayerGroupingResponse = {
  [masterPayorGroup: string]: string[];
};

type EditablePayerRow = {
  rowId: string;
  insurancePayerPrimary: string;
  assignedMasterPayorGroup: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const extractGroupedPayerMap = (payload: unknown): PayerGroupingResponse => {
  if (!isRecord(payload)) return {};

  const source = isRecord(payload.data) ? payload.data : payload;
  const grouped: PayerGroupingResponse = {};

  Object.keys(source).forEach((key) => {
    const maybeArray = source[key];
    if (!Array.isArray(maybeArray)) return;

    const names: string[] = maybeArray
      .filter((item) => typeof item === 'string')
      .map((item) => String(item));

    grouped[key] = names;
  });

  return grouped;
};

const extractMasterPayors = (payload: unknown, groupedMap: PayerGroupingResponse): string[] => {
  const groupedKeys = Object.keys(groupedMap);

  if (isRecord(payload)) {
    if (Array.isArray(payload.MasterPayors)) {
      const fromResponse = payload.MasterPayors.map((value) => String(value));
      return Array.from(new Set<string>([...fromResponse, ...groupedKeys]));
    }
    if (isRecord(payload.data) && Array.isArray(payload.data.MasterPayors)) {
      const fromResponse = payload.data.MasterPayors.map((value) => String(value));
      return Array.from(new Set<string>([...fromResponse, ...groupedKeys]));
    }
  }

  return groupedKeys;
};

const flattenPayerGroupings = (
  payload: PayerGroupingResponse
): EditablePayerRow[] => {
  const rows: EditablePayerRow[] = [];

  for (const masterPayorGroup in payload) {
    if (!Object.prototype.hasOwnProperty.call(payload, masterPayorGroup)) {
      continue;
    }

    const payers = payload[masterPayorGroup] || [];
    for (let index = 0; index < payers.length; index += 1) {
      const name = payers[index];
      rows.push({
        rowId: masterPayorGroup + '::' + String(index) + '::' + name,
        insurancePayerPrimary: name,
        assignedMasterPayorGroup: masterPayorGroup,
      });
    }
  }

  return rows.sort((a, b) => a.insurancePayerPrimary.localeCompare(b.insurancePayerPrimary));
};

const AccountSettings: React.FC = () => {
  const { state, dispatch } = useScenarioContext();
  const { practiceOptions, isLoading: isLoadingPractices, orgDropdownLocked, isDemoMode } = usePracticeData();
  const toast = useToast();

  const [selectedAccountNumber, setSelectedAccountNumber] = useState<string>('');

  const [showPayerGroupingModal, setShowPayerGroupingModal] = useState(false);
  const [showFeeScheduleModal, setShowFeeScheduleModal] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [payerRows, setPayerRows] = useState<EditablePayerRow[]>([]);
  const [originalPayerRows, setOriginalPayerRows] = useState<EditablePayerRow[]>([]);
  const [masterPayorGroups, setMasterPayorGroups] = useState<string[]>([]);
  const [originalMasterPayorGroups, setOriginalMasterPayorGroups] = useState<string[]>([]);
  const [isLoadingPayerGroupings, setIsLoadingPayerGroupings] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [payerGroupingError, setPayerGroupingError] = useState<string | null>(null);

  const [newMasterPayorName, setNewMasterPayorName] = useState('');
  const [renamingKey, setRenamingKey] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [uploadActions, setUploadActions] = useState<{ onDiscard: () => void; onApply: () => void; canApply: boolean; onUploadDifferent: () => void } | null>(null);

  // Tracks current group name -> its original (pre-edit) name so a rename can carry
  // its fee-schedule rates over instead of orphaning them. Only renamed groups appear.
  const [groupOrigin, setGroupOrigin] = useState<Record<string, string>>({});

  // Normalized master-group name -> number of product families that have a fee-schedule
  // rate for it. Powers the inline "fee schedule exists?" indicator on the Master Payers
  // tab and the save-time reconciliation check. Loaded alongside the payer groupings.
  const [ratesByGroupNorm, setRatesByGroupNorm] = useState<Map<string, number>>(new Map());
  // True only when the fee-schedule coverage fetch succeeded. If it failed we can't tell
  // which rates a group rename/removal would strand, so we refuse to save (see handleSave).
  const [coverageLoaded, setCoverageLoaded] = useState(false);

  // Reconciliation dialog shown on save when a rename/removal would affect fee schedules.
  const [reconcilePlan, setReconcilePlan] = useState<ReconcilePlan | null>(null);
  const [showReconcileDialog, setShowReconcileDialog] = useState(false);

  useEffect(() => {
    if (isLoadingPractices || practiceOptions.length === 0) return;
    // Default to the generator's cached logged_in_user if available, otherwise first option
    const generatorUser = state.generatorState?.form?.logged_in_user;
    const defaultAccount = generatorUser && practiceOptions.some((o) => o.value === generatorUser)
      ? generatorUser
      : practiceOptions[0].value;
    if (defaultAccount && !selectedAccountNumber) {
      setSelectedAccountNumber(defaultAccount);
    }
  }, [isLoadingPractices, practiceOptions]);

  const loadPayerGroupings = async () => {
    if (!selectedAccountNumber) {
      setPayerRows([]);
      setMasterPayorGroups([]);
      setPayerGroupingError('No active account selected. Please select an organization first.');
      return;
    }

    setIsLoadingPayerGroupings(true);
    setPayerGroupingError(null);

    try {
      const response = await ApiService.getPayerGroupings(selectedAccountNumber);
      const normalizedResponse = response as unknown;
      const groupedMap = extractGroupedPayerMap(normalizedResponse);
      const rows = flattenPayerGroupings(groupedMap);
      const groups = extractMasterPayors(normalizedResponse, groupedMap);

      setPayerRows(rows);
      setOriginalPayerRows(rows);
      setMasterPayorGroups(groups);
      setOriginalMasterPayorGroups(groups);
      setGroupOrigin({});
    } catch (error) {
      setPayerRows([]);
      setMasterPayorGroups([]);
      setPayerGroupingError(handleApiError(error));
    } finally {
      setIsLoadingPayerGroupings(false);
    }

    // Best-effort: which master groups actually have fee-schedule rates. Drives the
    // inline indicator + save-time reconciliation. Never blocks the groupings view.
    try {
      const health = await ApiService.getFeeScheduleCoverage(selectedAccountNumber);
      const map = new Map<string, number>();
      for (const f of health.families) {
        const seen = new Set<string>();
        f.mapped_groups.forEach((g) => seen.add(normGroup(g)));
        f.orphaned_rates.forEach((g) => seen.add(normGroup(g)));
        seen.forEach((n) => map.set(n, (map.get(n) ?? 0) + 1));
      }
      setRatesByGroupNorm(map);
      setCoverageLoaded(true);
    } catch {
      setRatesByGroupNorm(new Map());
      setCoverageLoaded(false);
    }
  };

  useEffect(() => {
    // Load groupings when EITHER modal opens: the Fee Schedules editor needs
    // masterPayorGroups (+ coverage counts) too, otherwise it opens with an empty
    // master-group list and can't surface every payer's rate row.
    if ((!showPayerGroupingModal && !showFeeScheduleModal) || !selectedAccountNumber) return;
    loadPayerGroupings();
  }, [showPayerGroupingModal, showFeeScheduleModal, selectedAccountNumber]);

  const masterPayorOptions = useMemo(() => {
    // Sort masterPayorGroups alphabetically (case-insensitive)
    const sortedGroups = [...masterPayorGroups].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    return sortedGroups.map((value) => ({ label: value, value }));
  }, [masterPayorGroups]);

  const hasAssignmentChanges = useMemo(() => {
    if (payerRows.length !== originalPayerRows.length) return true;
    const originalMap = new Map(originalPayerRows.map((r) => [r.insurancePayerPrimary, r.assignedMasterPayorGroup]));
    return payerRows.some((r) => originalMap.get(r.insurancePayerPrimary) !== r.assignedMasterPayorGroup);
  }, [payerRows, originalPayerRows]);

  const hasMasterPayorChanges = useMemo(() => {
    if (masterPayorGroups.length !== originalMasterPayorGroups.length) return true;
    for (let i = 0; i < masterPayorGroups.length; i += 1) {
      if (masterPayorGroups[i] !== originalMasterPayorGroups[i]) return true;
    }
    return false;
  }, [masterPayorGroups, originalMasterPayorGroups]);

  const payerCountByGroup = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const row of payerRows) {
      counts[row.assignedMasterPayorGroup] = (counts[row.assignedMasterPayorGroup] ?? 0) + 1;
    }
    return counts;
  }, [payerRows]);

  const activeTabHasChanges = activeTab === 0 ? hasAssignmentChanges : activeTab === 1 ? hasMasterPayorChanges : uploadActions !== null;
  const hasAnyChanges = hasAssignmentChanges || hasMasterPayorChanges || uploadActions !== null;

  // Only "Payer Assignments" is usable until the account has payer plans loaded
  const plansLoaded = payerRows.length > 0;

  useEffect(() => {
    if (!plansLoaded && activeTab !== 0) setActiveTab(0);
  }, [plansLoaded]);

  const doClosePayerGroupingModal = () => {
    setShowPayerGroupingModal(false);
    setTimeout(() => setActiveTab(0), 300);
    setRenamingKey(null);
    setNewMasterPayorName('');
    setUploadActions(null);
    setGroupOrigin({});
  };

  const handlePayerGroupingModalHide = () => {
    if (hasAnyChanges) {
      confirmDialog({
        message: 'You have unsaved changes. Discard them and close?',
        header: 'Unsaved Changes',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Discard & Close',
        rejectLabel: 'Stay',
        acceptClassName: 'p-button-danger',
        accept: doClosePayerGroupingModal,
      });
      return;
    }
    doClosePayerGroupingModal();
  };

  const handleTabChange = (nextIndex: number) => {
    if (nextIndex === activeTab) return;
    if (activeTabHasChanges) {
      confirmDialog({
        message: 'You have unsaved changes on this tab. Discard them?',
        header: 'Unsaved Changes',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Discard',
        rejectLabel: 'Stay',
        acceptClassName: 'p-button-danger',
        accept: () => {
          if (activeTab === 0) {
            setPayerRows(originalPayerRows);
          } else {
            setMasterPayorGroups(originalMasterPayorGroups);
            setPayerRows(originalPayerRows);
            setGroupOrigin({});
          }
          setRenamingKey(null);
          setActiveTab(nextIndex);
        },
      });
      return;
    }
    setActiveTab(nextIndex);
  };

  const buildGrouped = (): Record<string, string[]> => {
    const grouped: Record<string, string[]> = {};
    for (const g of masterPayorGroups) grouped[g] = [];
    for (const row of payerRows) {
      const group = row.assignedMasterPayorGroup;
      if (!grouped[group]) grouped[group] = [];
      grouped[group].push(row.insurancePayerPrimary);
    }
    return grouped;
  };

  const persistGroupings = async (reconciliation?: PayerGroupingReconciliation) => {
    setIsSaving(true);
    try {
      await ApiService.savePayerGroupings(selectedAccountNumber, buildGrouped(), reconciliation);
      await loadPayerGroupings();
      toast.current?.show({
        severity: 'success',
        summary: 'Saved',
        detail: 'Payer groupings saved successfully.',
        life: 3000,
      });
    } catch (error) {
      console.error('Failed to save payer groupings:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Save Failed',
        detail: handleApiError(error),
        life: 4000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    // Detect group renames / removals that would strand fee-schedule rates keyed to
    // the old name. Renames are tracked live (groupOrigin); removals are whatever
    // original group is no longer present and wasn't renamed.
    const currentNorms = new Set(masterPayorGroups.map(normGroup));
    const originalNorms = new Set(originalMasterPayorGroups.map(normGroup));

    const renameCandidates: { from: string; to: string }[] = [];
    const renamedOriginNorms = new Set<string>();
    for (const g of masterPayorGroups) {
      const origin = groupOrigin[g];
      if (origin && normGroup(origin) !== normGroup(g) && originalNorms.has(normGroup(origin))) {
        renameCandidates.push({ from: origin, to: g });
        renamedOriginNorms.add(normGroup(origin));
      }
    }
    const removedGroups = originalMasterPayorGroups.filter(
      (g) => !currentNorms.has(normGroup(g)) && !renamedOriginNorms.has(normGroup(g))
    );

    // No structural change -> save straight through (unchanged behaviour).
    if (renameCandidates.length === 0 && removedGroups.length === 0) {
      await persistGroupings();
      return;
    }

    // A group was renamed/removed, but we could not load the fee-schedule coverage that
    // tells us which rates would be stranded. Rather than save blindly (and silently
    // orphan a rate), refuse and ask the user to retry once coverage can load.
    if (!coverageLoaded) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Could not verify fee schedules',
        detail: 'We could not check which fee-schedule rates your group changes affect. Please close and reopen this dialog, then try again.',
        life: 5000,
      });
      return;
    }

    // Only groups that actually carry fee-schedule rates need reconciliation. Reuse the
    // presence map already loaded with the groupings (no extra round-trip).
    const renames = renameCandidates
      .filter((r) => ratesByGroupNorm.has(normGroup(r.from)))
      .map((r) => ({ from: r.from, to: r.to, familyCount: ratesByGroupNorm.get(normGroup(r.from)) ?? 0 }));

    const removed: RemovedGroupDecision[] = removedGroups
      .filter((g) => ratesByGroupNorm.has(normGroup(g)))
      .map((g) => ({
        group: g,
        familyCount: ratesByGroupNorm.get(normGroup(g)) ?? 0,
        // Default to reassign (with no target yet) so Save stays disabled until the user
        // consciously picks reassign-to-a-group or delete — never a silent drop.
        action: 'reassign' as const,
      }));

    if (renames.length === 0 && removed.length === 0) {
      await persistGroupings();
      return;
    }

    setReconcilePlan({ renames, removed });
    setShowReconcileDialog(true);
  };

  const updateRemovedDecision = (group: string, patch: Partial<RemovedGroupDecision>) => {
    setReconcilePlan((prev) =>
      prev
        ? { ...prev, removed: prev.removed.map((d) => (d.group === group ? { ...d, ...patch } : d)) }
        : prev
    );
  };

  const reconcileValid = useMemo(() => {
    if (!reconcilePlan) return false;
    return reconcilePlan.removed.every((d) => d.action !== 'reassign' || !!d.target);
  }, [reconcilePlan]);

  const applyReconciliation = async () => {
    if (!reconcilePlan) return;
    const renames: Record<string, string> = {};
    const dropGroups: string[] = [];
    for (const r of reconcilePlan.renames) renames[r.from] = r.to;
    for (const d of reconcilePlan.removed) {
      if (d.action === 'reassign' && d.target) renames[d.group] = d.target;
      else if (d.action === 'drop') dropGroups.push(d.group);
    }
    const reconciliation: PayerGroupingReconciliation = {};
    if (Object.keys(renames).length) reconciliation.renames = renames;
    if (dropGroups.length) reconciliation.drop_groups = dropGroups;

    setShowReconcileDialog(false);
    setReconcilePlan(null);
    await persistGroupings(Object.keys(reconciliation).length ? reconciliation : undefined);
  };

  const handleAddMasterPayor = () => {
    const trimmed = newMasterPayorName.trim();
    if (!trimmed) return;
    // Match by normGroup (not just lowercase) so spacing/punctuation variants like
    // "Managed Care" vs "ManagedCare" can't create two groups that collide downstream.
    if (masterPayorGroups.some((g) => normGroup(g) === normGroup(trimmed))) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Already exists',
        detail: `"${trimmed}" is already a master payer group.`,
        life: 3000,
      });
      return;
    }
    setMasterPayorGroups((prev) => [...prev, trimmed]);
    setNewMasterPayorName('');
  };

  const handleRenameMasterPayor = (oldName: string) => {
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === oldName) {
      setRenamingKey(null);
      return;
    }
    // Same normGroup-based uniqueness as add: block renaming into a name that already
    // exists OR that collides with one under the system's matching normalization.
    if (masterPayorGroups.some((g) => normGroup(g) === normGroup(trimmed) && g !== oldName)) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Already exists',
        detail: `"${trimmed}" is already a master payer group.`,
        life: 3000,
      });
      setRenamingKey(null);
      return;
    }
    setMasterPayorGroups((prev) => prev.map((g) => (g === oldName ? trimmed : g)));
    setPayerRows((prev) =>
      prev.map((row) =>
        row.assignedMasterPayorGroup === oldName
          ? { ...row, assignedMasterPayorGroup: trimmed }
          : row
      )
    );
    // Preserve the original (pre-edit) name across chained renames so its
    // fee-schedule rates can be carried over on save.
    setGroupOrigin((prev) => {
      const next = { ...prev };
      const origin = next[oldName] ?? oldName;
      delete next[oldName];
      if (normGroup(origin) !== normGroup(trimmed)) {
        next[trimmed] = origin;
      }
      return next;
    });
    setRenamingKey(null);
  };

  const handleDeleteMasterPayor = (name: string) => {
    const affectedCount = payerRows.filter((row) => row.assignedMasterPayorGroup === name).length;
    confirmDialog({
      message: affectedCount > 0
        ? `"${name}" has ${affectedCount} payer(s) assigned. They will be moved to "Other/Unmapped". Continue?`
        : `Delete "${name}"? This master payer has no payers assigned.`,
      header: 'Delete Master Payer',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => {
        const fallback = 'Other/Unmapped';
        if (masterPayorGroups.indexOf(fallback) === -1) {
          setMasterPayorGroups((prev) => [...prev.filter((g) => g !== name), fallback]);
        } else {
          setMasterPayorGroups((prev) => prev.filter((g) => g !== name));
        }
        setPayerRows((prev) =>
          prev.map((row) =>
            row.assignedMasterPayorGroup === name
              ? { ...row, assignedMasterPayorGroup: fallback }
              : row
          )
        );
        setGroupOrigin((prev) => {
          if (!(name in prev)) return prev;
          const next = { ...prev };
          delete next[name];
          return next;
        });
      },
    });
  };

  const handleMasterPayorChange = (rowId: string, nextMasterPayorGroup: string) => {
    setPayerRows((previousRows) => {
      return previousRows.map((row) => {
        if (row.rowId !== rowId) return row;
        return {
          ...row,
          assignedMasterPayorGroup: nextMasterPayorGroup,
        };
      });
    });
  };

  return (
    <div className="account-settings-container">
      <ConfirmDialog />
      <Card title="Account Settings">
        <div className="settings-org-row">
          <label htmlFor="practice-select">Organization</label>
          {isLoadingPractices ? (
            <ProgressSpinner style={{ width: '24px', height: '24px' }} strokeWidth="6" />
          ) : (
            <Dropdown
              id="practice-select"
              value={selectedAccountNumber}
              options={practiceOptions}
              disabled={orgDropdownLocked}
              onChange={(e) => {
                const acct = String(e.value || '');
                setSelectedAccountNumber(acct);
                if (acct && acct !== state.generatorState.form?.logged_in_user) {
                  // Switching the account must reset the generator form the same way the
                  // generate page does. Carrying the previous account's categories/families
                  // into the new account leaves the generate page with stale, mismatched
                  // selections and throws when it loads data for the new account.
                  dispatch(scenarioActions.setGeneratorState({
                    userName: '',
                    form: { logged_in_user: acct, categories: [], max_scenarios: 25 },
                    selectedFamilies: {},
                  }));
                }
              }}
              placeholder="Select a practice"
              className="w-20rem"
              itemTemplate={(option) => (
                <div className="flex align-items-center gap-2">
                  {option.isCorporate ? (
                    <i className="pi pi-building" style={{ fontSize: '1.1rem', color: '#173A63' }} />
                  ) : option.isChild ? (
                    <i className="pi pi-home" style={{ fontSize: '0.9rem', color: '#6c757d', marginLeft: '1rem' }} />
                  ) : (
                    <i className="pi pi-briefcase" style={{ fontSize: '0.9rem', color: '#495057' }} />
                  )}
                  <span>{option.label}</span>
                </div>
              )}
            />
          )}
        </div>

        <Divider />

        <div className="settings-action-row">
          <div className="settings-action-info">
            <p className="settings-action-title">Payer Groupings</p>
            <p className="settings-action-desc">Assign insurance payers to master payer groups used in scenario modeling.</p>
          </div>
          <Button
            label="Manage Payer Groupings"
            icon="pi pi-sitemap"
            onClick={() => setShowPayerGroupingModal(true)}
          />
        </div>

        <Divider />

        <div className="settings-action-row">
          <div className="settings-action-info">
            <p className="settings-action-title">Fee Schedules</p>
            <p className="settings-action-desc">Customize reimbursement rates by product family and payer.</p>
          </div>
          <Button
            label="Manage Fee Schedules"
            icon="pi pi-dollar"
            onClick={() => setShowFeeScheduleModal(true)}
            style={{ minWidth: '16.25rem' }}
          />
        </div>
      </Card>

      <Dialog
        header="Payer Groupings"
        visible={showPayerGroupingModal}
        style={{ width: '80vw', maxWidth: '1200px' }}
        contentStyle={{ overflow: 'hidden' }}
        className="payer-grouping-dialog"
        modal
        onHide={handlePayerGroupingModalHide}
        footer={
          <div className="flex justify-content-end" style={{ paddingTop: '0.75rem' }}>
            {activeTab !== 2 ? (
              // The tooltip hangs off this span, not off the Button via
              // tooltipOptions.showOnDisabled. showOnDisabled makes PrimeReact
              // re-parent the button into a raw wrapper div outside React, and this
              // button unmounts on the tab switch below -- React then removes it from
              // the wrong parent and the app dies with a removeChild NotFoundError.
              <>
                <Tooltip target=".demo-tip-groupings-save" position="top" />
                <span
                  className={`inline-flex${isDemoMode ? ' demo-tip-groupings-save' : ''}`}
                  data-pr-tooltip={isDemoMode ? 'Read-only demo account' : undefined}
                >
                  <Button
                    label="Save"
                    icon={isSaving ? 'pi pi-spin pi-spinner' : 'pi pi-save'}
                    onClick={handleSave}
                    // Demo accounts are read-only server-side (423).
                    disabled={isLoadingPayerGroupings || isSaving || !activeTabHasChanges || isDemoMode}
                  />
                </span>
              </>
            ) : uploadActions ? (
              <div className="flex justify-content-between align-items-center w-full">
                <Button
                  label="Upload Different File"
                  icon="pi pi-upload"
                  severity="secondary"
                  text
                  onClick={uploadActions.onUploadDifferent}
                />
                <div className="flex gap-2">
                  <Button
                    label="Discard"
                    severity="secondary"
                    outlined
                    onClick={() =>
                      confirmDialog({
                        message: 'Discard the uploaded file and all pending changes?',
                        header: 'Discard Upload',
                        icon: 'pi pi-exclamation-triangle',
                        acceptLabel: 'Discard',
                        rejectLabel: 'Cancel',
                        acceptClassName: 'p-button-danger',
                        accept: uploadActions.onDiscard,
                      })
                    }
                  />
                  <Button
                    label="Apply Changes"
                    icon="pi pi-check"
                    onClick={uploadActions.onApply}
                    disabled={!uploadActions.canApply}
                  />
                </div>
              </div>
            ) : null}
          </div>
        }
      >
        {isLoadingPayerGroupings && (
          <div className="payer-grouping-status">
            <ProgressSpinner style={{ width: '36px', height: '36px' }} strokeWidth="6" />
          </div>
        )}

        {!isLoadingPayerGroupings && payerGroupingError && (
          <div className="payer-grouping-status payer-grouping-status-error">
            {payerGroupingError}
          </div>
        )}

        {!isLoadingPayerGroupings && !payerGroupingError && (
          <TabView activeIndex={activeTab} onTabChange={(e) => handleTabChange(e.index)}>
            <TabPanel header="Payer Assignments">
              <div className="payer-grouping-modal-body">
                {plansLoaded ? (
                  <p className="text-600 mt-0 mb-3">
                    Choose a Master Payer Group for each payer.
                  </p>
                ) : (
                  <div className="payer-grouping-status">No payer groupings found for this account.</div>
                )}

                {payerRows.map((row) => (
                  <div className="payer-row" key={row.rowId}>
                    <div className="payer-row-main">
                      <div className="payer-name">{row.insurancePayerPrimary}</div>
                    </div>
                    <div className="payer-row-action">
                      <Dropdown
                        value={row.assignedMasterPayorGroup}
                        options={masterPayorOptions}
                        onChange={(event) =>
                          handleMasterPayorChange(row.rowId, String(event.value || ''))
                        }
                        placeholder="Select Master Payer Group"
                        className="w-full"
                        appendTo="self" // Ensures dropdown stays within the modal
                      />
                    </div>
                  </div>
                ))}
              </div>
            </TabPanel>

            <TabPanel header="Master Payers" disabled={!plansLoaded}>
              <div className="master-payor-modal-body">
                <Tooltip target=".master-payor-count" />
                <Tooltip target=".master-payor-fs" />
                <div className="master-payor-add-row">
                  <InputText
                    value={newMasterPayorName}
                    onChange={(e) => setNewMasterPayorName(e.target.value)}
                    placeholder="New Master Payer name"
                    className="w-full"
                    maxLength={50}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddMasterPayor(); }}
                  />
                  <Button
                    icon="pi pi-plus"
                    onClick={handleAddMasterPayor}
                    disabled={!newMasterPayorName.trim()}
                  />
                </div>

                <Divider />

                {masterPayorGroups.length === 0 && (
                  <div className="payer-grouping-status">No master payers defined.</div>
                )}

                {[...masterPayorGroups].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })).map((group) => {
                  const count = payerCountByGroup[group] ?? 0;
                  // A group has a fee schedule if a rate exists for it, or for the
                  // original name it was renamed from (that rate carries over on save).
                  const origin = groupOrigin[group];
                  const hasFeeSchedule =
                    ratesByGroupNorm.has(normGroup(group)) ||
                    (!!origin && ratesByGroupNorm.has(normGroup(origin)));
                  return (
                    <div className={`master-payor-row${count === 0 ? ' master-payor-row--empty' : ''}`} key={group}>
                      {renamingKey === group ? (
                        <div className="master-payor-rename">
                          <InputText
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            className="w-full"
                            maxLength={50}
                            autoFocus
                            onKeyDown={(e) => { if (e.key === 'Enter') handleRenameMasterPayor(group); }}
                          />
                          <Button
                            icon="pi pi-check"
                            severity="success"
                            text
                            onClick={() => handleRenameMasterPayor(group)}
                          />
                          <Button
                            icon="pi pi-times"
                            severity="secondary"
                            text
                            onClick={() => setRenamingKey(null)}
                          />
                        </div>
                      ) : (
                        <>
                          <span className="master-payor-name">
                            {group}
                            <span
                              className={`master-payor-count${count === 0 ? ' master-payor-count--zero' : ''}`}
                              data-pr-tooltip={count === 0 ? 'No insurance payers assigned' : `${count} insurance payer${count !== 1 ? 's' : ''} assigned to this group`}
                              data-pr-position="top"
                            >
                              {count}
                            </span>
                            {coverageLoaded && (
                              <span
                                className={`master-payor-fs master-payor-fs--${hasFeeSchedule ? 'yes' : 'no'}`}
                                data-pr-tooltip={hasFeeSchedule
                                  ? 'A fee schedule rate exists for this group'
                                  : 'No fee schedule rate for this group — reimbursement falls back to Medicare'}
                                data-pr-position="top"
                              >
                                <i className={`pi ${hasFeeSchedule ? 'pi-check-circle' : 'pi-exclamation-circle'}`} />
                                {hasFeeSchedule ? 'Fee schedule' : 'No fee schedule'}
                              </span>
                            )}
                          </span>
                          {group !== 'Other/Unmapped' && (
                            <div className="master-payor-actions">
                              <Button
                                icon="pi pi-pencil"
                                severity="secondary"
                                text
                                onClick={() => { setRenamingKey(group); setRenameValue(group); }}
                              />
                              <Button
                                icon="pi pi-trash"
                                severity="danger"
                                text
                                style={{ color: 'var(--red-500)' }}
                                onClick={() => handleDeleteMasterPayor(group)}
                              />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </TabPanel>

            <TabPanel header="Bulk Upload" disabled={!plansLoaded}>
              <PayerGroupingUpload
                existingPayers={payerRows.map((r) => ({
                  insurancePayerPrimary: r.insurancePayerPrimary,
                  assignedMasterPayorGroup: r.assignedMasterPayorGroup,
                }))}
                existingGroups={masterPayorGroups}
                onUploadStateChange={(actions) => setUploadActions(actions)}
                onApply={(uploadedRows, uploadedGroups) => {
                  const newRows: EditablePayerRow[] = uploadedRows.map((r, idx) => ({
                    rowId: r.assignedMasterPayorGroup + '::' + String(idx) + '::' + r.insurancePayerPrimary,
                    insurancePayerPrimary: r.insurancePayerPrimary,
                    assignedMasterPayorGroup: r.assignedMasterPayorGroup,
                  }));
                  setPayerRows(newRows);
                  setMasterPayorGroups(uploadedGroups);
                  // A bulk upload replaces the whole set — rename lineage can't be
                  // tracked through it, so any removed group is reconciled on save.
                  setGroupOrigin({});
                  setActiveTab(0);
                }}
              />
            </TabPanel>
          </TabView>
        )}
      </Dialog>
      <FeeScheduleModal
        visible={showFeeScheduleModal}
        accountNumber={selectedAccountNumber}
        masterPayorGroups={masterPayorGroups}
        onHide={() => setShowFeeScheduleModal(false)}
      />

      {/* Reconciliation prompt — shown on save when a group rename/removal affects fee schedules */}
      <Dialog
        header="Keep fee schedules in sync"
        visible={showReconcileDialog}
        style={{ width: '90vw', maxWidth: '640px' }}
        className="reconcile-dialog"
        modal
        onHide={() => {
          setShowReconcileDialog(false);
          setReconcilePlan(null);
        }}
        footer={
          <div className="flex justify-content-end gap-2" style={{ paddingTop: '0.5rem' }}>
            <Button
              label="Cancel"
              severity="secondary"
              outlined
              onClick={() => {
                setShowReconcileDialog(false);
                setReconcilePlan(null);
              }}
              disabled={isSaving}
            />
            <Button
              label="Apply & Save"
              icon={isSaving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
              onClick={applyReconciliation}
              disabled={isSaving || !reconcileValid}
            />
          </div>
        }
      >
        {reconcilePlan && (
          <div className="reconcile-body">
            <p className="reconcile-intro">
              Fee-schedule rates are linked to payer groups by name. Your changes affect groups that
              have rates — choose what happens so no rate is silently lost.
            </p>

            {reconcilePlan.renames.length > 0 && (
              <div className="reconcile-section">
                <div className="reconcile-section-title">
                  <i className="pi pi-arrow-right-arrow-left" /> Renamed — rates move automatically
                </div>
                {reconcilePlan.renames.map((r) => (
                  <div className="reconcile-rename-row" key={`${r.from}->${r.to}`}>
                    <span className="reconcile-old">{r.from}</span>
                    <i className="pi pi-arrow-right reconcile-arrow" />
                    <span className="reconcile-new">{r.to}</span>
                    <span className="reconcile-count">
                      {r.familyCount} product famil{r.familyCount === 1 ? 'y' : 'ies'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {reconcilePlan.removed.length > 0 && (
              <div className="reconcile-section">
                <div className="reconcile-section-title">
                  <i className="pi pi-exclamation-triangle" /> Removed — these groups still have rates
                </div>
                {reconcilePlan.removed.map((d) => (
                  <div className="reconcile-removed-row" key={d.group}>
                    <div className="reconcile-removed-head">
                      <span className="reconcile-old">{d.group}</span>
                      <span className="reconcile-count">
                        {d.familyCount} product famil{d.familyCount === 1 ? 'y' : 'ies'}
                      </span>
                    </div>
                    <div className="reconcile-removed-controls">
                      <div className="reconcile-choice-row">
                        {(['reassign', 'drop'] as const).map((act) => (
                          <button
                            key={act}
                            type="button"
                            className={`reconcile-choice${d.action === act ? ' reconcile-choice--active' : ''}`}
                            onClick={() => updateRemovedDecision(d.group, { action: act })}
                          >
                            {act === 'reassign' ? 'Reassign rates' : 'Delete rates'}
                          </button>
                        ))}
                      </div>
                      {d.action === 'reassign' && (
                        <Dropdown
                          value={d.target ?? null}
                          options={masterPayorGroups
                            .slice()
                            .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
                            .map((g) => ({ label: g, value: g }))}
                          onChange={(e) => updateRemovedDecision(d.group, { target: String(e.value || '') })}
                          placeholder="Move rates to…"
                          className="reconcile-target w-full"
                          appendTo={document.body}
                        />
                      )}
                      <p className="reconcile-choice-hint">
                        {d.action === 'reassign'
                          ? 'Rates move to the selected group.'
                          : 'Rates are permanently removed.'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default AccountSettings;
